/**
 * Okos lekérdezés in credits (owner's decisions, 2026-11-04). The database decides
 * (20261220100000_okos_lekerdezes.sql: ai_credit_cost, ai_credits_internal, ai_credit_take, buy_ai_credits,
 * ai_reward_note, ai_top_questions); these mirror its defaults and rules for the app's texts and tests. A
 * database test keeps CREDIT_DEFAULTS equal to the migration's settings.
 *
 * Toward the workshop everything is a whole number of credits ("kredit"), never forint; the numbers are
 * chosen so that they do not convert to forint in the head. The forint cost stays internal (aiLimits.ts).
 */
import type { Plan } from "./features";

export interface CreditCosts {
  /** One "Kérdezd az MI-t" question or a quick lookup pack. */
  simple: number;
  /** A heavy answer: the fuse sheet or the timing sheet. */
  heavy: number;
  /** Everyday help (registration photo, dictation, company lookup) over its free monthly count. */
  helper: number;
}

export interface CreditPack {
  credits: number;
  /** Net price in forint (VAT on top). */
  netHuf: number;
}

export interface CreditSettings {
  costs: CreditCosts;
  /** Included credits a month per plan; used first, they expire at the end of the month. */
  allowance: Record<Plan, number>;
  /** Included credits for the whole trial. */
  trial: number;
  packs: CreditPack[];
  /** Credits a workshop may buy per calendar month without the operator. */
  monthlyPurchaseLimit: number;
  /**
   * A lookup that fails or finds nothing gives its credits back only when its real cost stayed under this
   * (forint, internal; owner's decision 2026-11-04 12:49). Purchased and gift credits never expire.
   */
  refundMaxCostHuf: number;
  /** "Aki ad, kap": every rewardEvery votes or tips in a month earn rewardCredits, at most rewardMonthlyMax. */
  rewardEvery: number;
  rewardCredits: number;
  rewardMonthlyMax: number;
  /** A question is shown among the five most asked only when at least this many workshops asked it. */
  topMinWorkshops: number;
}

export const CREDIT_DEFAULTS: CreditSettings = {
  costs: { simple: 7, heavy: 14, helper: 1 },
  allowance: { basic: 75, medium: 225, premium: 735 },
  trial: 150,
  packs: [{ credits: 150, netHuf: 2000 }],
  monthlyPurchaseLimit: 750,
  refundMaxCostHuf: 10,
  rewardEvery: 10,
  rewardCredits: 7,
  rewardMonthlyMax: 35,
  topMinWorkshops: 3,
};

/** The packs that give a heavy answer (2 × a simple lookup by default). */
export const HEAVY_PACKS: readonly string[] = ["fuses", "timing"];

/** Credits of one lookup (same as public.ai_credit_cost). */
export function creditCost(costs: CreditCosts, kind: "pack" | "question" | "helper", pack?: string | null): number {
  if (kind === "helper") return costs.helper;
  if (kind === "pack" && pack && HEAVY_PACKS.includes(pack)) return costs.heavy;
  return costs.simple;
}

/** The old forint credit in credits at the first pack's rate (150 credits for 2000 Ft), rounded up (public.ai_huf_to_credits). */
export function hufToCredits(huf: number): number {
  return Math.ceil((huf * 150) / 2000);
}

/** The reward of a month for this many counted votes and tips (public.ai_reward_note). */
export function rewardEarned(s: Pick<CreditSettings, "rewardEvery" | "rewardCredits" | "rewardMonthlyMax">, actions: number): number {
  return Math.min(Math.floor(actions / Math.max(s.rewardEvery, 1)) * s.rewardCredits, s.rewardMonthlyMax);
}

/** How many more votes or tips earn the next reward this month; null when the month's maximum is reached. */
export function rewardToNext(s: Pick<CreditSettings, "rewardEvery" | "rewardCredits" | "rewardMonthlyMax">, actions: number): number | null {
  if (rewardEarned(s, actions) >= s.rewardMonthlyMax) return null;
  const every = Math.max(s.rewardEvery, 1);
  return every - (actions % every);
}

/**
 * Takes the credits of a lookup as the database does (public.ai_credit_take): the included credits first,
 * then the purchased (gift, converted) ones, the oldest first. Returns the new state, or null when they do
 * not cover it.
 */
export function takeCredits(
  allowanceLeft: number,
  grants: readonly { credits: number; grantedAt: string }[],
  cost: number,
): { allowanceLeft: number; grants: { credits: number; grantedAt: string }[] } | null {
  const total = allowanceLeft + grants.reduce((a, g) => a + g.credits, 0);
  if (total < cost) return null;
  const fromAllowance = Math.min(cost, allowanceLeft);
  let need = cost - fromAllowance;
  const sorted = [...grants].sort((a, b) => a.grantedAt.localeCompare(b.grantedAt)).map((g) => ({ ...g }));
  for (const g of sorted) {
    if (need <= 0) break;
    const take = Math.min(need, g.credits);
    g.credits -= take;
    need -= take;
  }
  return { allowanceLeft: allowanceLeft - fromAllowance, grants: sorted.filter((g) => g.credits > 0) };
}

/** Why a pack cannot be bought now (same order as public.buy_ai_credits), or null. Numbers in credits. */
export function creditPurchaseRefusal(
  p: { trial: boolean; readOnly: boolean; status: string; boughtThisMonth: number; limit: number },
  credits: number,
): "READ_ONLY" | "AI_SUBSCRIPTION_BLOCKED" | "AI_PURCHASE_TRIAL" | "AI_PURCHASE_LIMIT" | null {
  if (p.readOnly) return "READ_ONLY";
  if (p.status === "overdue" || p.status === "suspended") return "AI_SUBSCRIPTION_BLOCKED";
  if (p.trial) return "AI_PURCHASE_TRIAL";
  if (p.boughtThisMonth + credits > p.limit) return "AI_PURCHASE_LIMIT";
  return null;
}

/**
 * "A leggyakoribb 5 kérdés" until there is enough real data (same as the seed in public.ai_top_questions):
 * quick packs ("pack:<key>") and ready questions ("ask:<key>"). Real statistics replace them automatically.
 */
export const TOP_QUESTION_SEED = ["pack:fuses", "pack:timing", "pack:torques", "ask:serviceReset", "ask:brakeFluid"] as const;

/** The ready questions the AI gets in English (the button's text comes from the locale: `topQuestion.<key>`). */
export const READY_QUESTIONS: Record<string, string> = {
  serviceReset: "How to reset the service interval indicator (oil service reminder) on this vehicle, step by step, with the tool or menu needed.",
  brakeFluid: "Brake fluid change procedure on this vehicle: fluid specification and quantity, bleeding order, and any special step (ABS/ESP unit bleeding with a diagnostic tool).",
};

/**
 * A mechanic's own question as a statistics key: lower case, letters and digits only, single spaces.
 * Same as normalizeQuestion in supabase/functions/_shared/aiCredits.ts (a test checks it).
 */
export function normalizeQuestion(q: string): string {
  return q
    .toLocaleLowerCase("hu")
    .normalize("NFC")
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Whether a question may count for the five most asked (and be kept as a shared answer): short, and nothing
 * that could be personal (a run of 4 or more digits, as in a plate, phone or VIN, an e-mail, a VIN like code).
 */
export function questionShareable(q: string): boolean {
  const n = normalizeQuestion(q);
  if (n.length < 5 || n.length > 100) return false;
  if (/@/.test(q) || /\d{4,}/.test(q.replace(/\s/g, ""))) return false;
  if (/\b[a-z]{2,4}[\s-]?([a-z]{2}[\s-]?)?\d{3}\b/i.test(q) || /\b[a-hj-npr-z0-9]{17}\b/i.test(q)) return false;
  return true;
}

/** The statistics item of an own question ("q:..."), or null when it must not be counted. */
export function questionItem(q: string): string | null {
  return questionShareable(q) ? `q:${normalizeQuestion(q)}`.slice(0, 120) : null;
}

/** Whether a failed or empty lookup gives its credits back (public.ai_lookup_finish): only when cheap. */
export function creditRefunded(found: boolean, realCostHuf: number, s: Pick<CreditSettings, "refundMaxCostHuf">): boolean {
  return !found && realCostHuf < s.refundMaxCostHuf;
}
